import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, HelpCircle, MessageSquareQuote, Paperclip, Quote, Sparkles } from 'lucide-react';
import type { AttachmentRequest, Draft } from '@mailpilot/shared';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { ResolveRequestDialog } from '../library/ResolveRequestDialog';
import { Link } from 'react-router-dom';

/** Explanation, supporting context, missing info and required attachments for an AI-generated draft. */
export function AiSuggestionPanel({ draft, onEvidence }: { draft: Draft; onEvidence?: (messageId: string) => void }) {
  const [resolving, setResolving] = useState<AttachmentRequest | null>(null);
  const needsRequests = draft.requiredAttachments.some((r) => !r.resolvedLibraryFileId && !r.resolvedUploadId);
  const requests = useQuery({
    queryKey: qk.attachmentRequests('open'),
    queryFn: () => api('GET /api/attachment-requests', { query: { status: 'open' } }),
    enabled: needsRequests,
  });
  const forDraft = (requests.data?.requests ?? []).filter((r) => r.draftId === draft.id);

  return (
    <div className="overflow-hidden rounded-xl border border-primary/15 bg-primary-soft/40">
      <div className="flex flex-wrap items-center gap-2 px-4 pt-3">
        <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-primary text-primary-fg">
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
        </span>
        <span className="text-[0.8125rem] font-semibold text-ink">AI suggestion</span>
        {draft.generatedBy && <span className="text-[0.6875rem] text-muted">by {draft.generatedBy}</span>}
        <span className="ml-auto text-[0.6875rem] text-muted">Review before sending — MailPilot never invents prices, commitments or file contents.</span>
      </div>
      <div className="space-y-3.5 px-4 pb-4 pt-2.5">
        {draft.explanation && <p className="max-w-prose text-[0.8125rem] leading-relaxed text-ink">{draft.explanation}</p>}

        {draft.supportingContext.length > 0 && (
          <div>
            <h4 className="mb-1.5 flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">
              <MessageSquareQuote className="h-3.5 w-3.5" aria-hidden /> Supporting context
            </h4>
            <ul className="flex flex-wrap gap-1.5">
              {draft.supportingContext.map((ev, i) => (
                <li key={i} className="max-w-full">
                  <button
                    type="button"
                    onClick={() => onEvidence?.(ev.messageId)}
                    className="inline-flex max-w-full items-center gap-1 rounded-md bg-surface px-2 py-1 text-[0.6875rem] font-medium text-primary-ink shadow-sm transition-colors hover:bg-primary-soft"
                    title="Show the supporting message"
                  >
                    <Quote className="h-3 w-3 shrink-0" aria-hidden />
                    <span className="max-w-[26rem] truncate italic">“{ev.quote}”</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {draft.missingInformation.length > 0 && (
          <div className="rounded-xl border border-warn/30 bg-warn-soft px-3.5 py-2.5 text-warn-ink">
            <h4 className="mb-1 flex items-center gap-1.5 text-xs font-semibold">
              <HelpCircle className="h-3.5 w-3.5" aria-hidden /> Missing information — fill these in yourself
            </h4>
            <ul className="list-disc space-y-0.5 pl-5 text-xs leading-relaxed">
              {draft.missingInformation.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          </div>
        )}

        {draft.requiredAttachments.length > 0 && (
          <div>
            <h4 className="mb-1.5 flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">
              <Paperclip className="h-3.5 w-3.5" aria-hidden /> Required attachments
            </h4>
            <ul className="space-y-1.5">
              {draft.requiredAttachments.map((ra, i) => {
                const resolved = !!(ra.resolvedLibraryFileId || ra.resolvedUploadId);
                const req = forDraft.find((r) => r.description === ra.description) ?? forDraft[i] ?? null;
                return (
                  <li key={i} className="flex flex-wrap items-center gap-3 rounded-xl border border-hairline bg-surface px-3 py-2.5">
                    <span className={resolved ? 'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-success-soft text-success-ink' : 'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-warn-soft text-warn-ink'}>
                      {resolved ? <CheckCircle2 className="h-4 w-4" aria-hidden /> : <AlertTriangle className="h-4 w-4" aria-hidden />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[0.8125rem] font-medium leading-snug text-ink">{ra.description}</p>
                      <p className="text-xs text-muted">{ra.reason}</p>
                    </div>
                    {resolved ? (
                      <Badge tone="success">Attached{ra.deliverAs ? ` as ${ra.deliverAs}` : ''}</Badge>
                    ) : req ? (
                      <Button size="sm" variant="primary" onClick={() => setResolving(req)}>
                        Choose file
                      </Button>
                    ) : (
                      <Link to="/library" className="text-xs font-semibold text-primary-ink hover:underline">
                        Resolve in Attachment Library →
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
            {draft.status === 'pending_attachment' && (
              <p className="mt-2 text-xs leading-relaxed text-muted">This reply stays pending until the requested file is chosen. An enabled acknowledgement can still be sent separately.</p>
            )}
          </div>
        )}
      </div>
      <ResolveRequestDialog request={resolving} open={!!resolving} onClose={() => setResolving(null)} />
    </div>
  );
}
